if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        const voucherCheckbox = document.querySelector('input[name="vouchers_needed"]:checked');
        const isVoucherSelected = voucherCheckbox && voucherCheckbox.value == 'voucher';
        // NOTE: isVoucherSelected is still used below to add the Voucher Fee and
        // the tiered Logo Print Fee — do not remove these two lines.

        // ── Voucher property appends removed 2026-09-08 ──────────────────────
        // This block used to append a SECOND copy of the voucher answers under
        // different keys than the ones the option-slider snippet writes:
        //
        //     properties[Vouchers]              (duplicate of the snippet's)
        //     properties[Vouchers Logo]         Yes/No — snippet writes "Have a logo"
        //     properties[Vouchers File Number]  snippet writes "Logo file number"
        //     properties[Vouchers Logo Colors]  question removed entirely
        //
        // So every voucher line carried each answer twice under two names, and
        // "Vouchers Logo" defaulted to "No" whenever the radio was unchecked —
        // which was always, since the snippet no longer pre-checks it.
        //
        // Single property writer principle: the snippet's hidden inputs in
        // .custom-properties-container are now the only writer. See
        // window.Reeves.voucher.syncBranches() in
        // snippets/product-option-slider--variant-option-images-2.liquid.
        //
        // OPEN — Stephen: confirm the Exporteo order_export.liquid.xml template
        // does not read the "Vouchers *" key spellings above. Its key
        // normalization is strip|downcase plus stripping spaces/underscores/
        // hyphens, so "Vouchers Logo" and "Have a logo" do NOT normalize to the
        // same thing. If the export does reference them, restore this block from
        // git rather than renaming the snippet's keys.
        // ─────────────────────────────────────────────────────────────────────

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        // getVoucherVariantIdByQuantity() removed with the tiered print-fee
        // add above. The tier variant ids live in the handoff notes; they are
        // not referenced by any code now, and leaving a tier table wired to
        // nothing invites someone to trust it.

        const addExtraProduct = async (variantId, quantity, properties = {}) => {
          const extraFormData = new FormData();
          extraFormData.append('id', variantId);
          extraFormData.append('quantity', quantity);

          for (const [key, value] of Object.entries(properties)) {
            extraFormData.append(`properties[${key}]`, value);
          }

          const extraConfig = fetchConfig('javascript');
          extraConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete extraConfig.headers['Content-Type'];
          extraConfig.body = extraFormData;

          try {
            const extraResponse = await fetch(`${routes.cart_add_url}`, extraConfig);
            const extraData = await extraResponse.json();

            if (extraData.status) {
              console.error(`Failed to add extra product ${variantId}:`, extraData);
              throw new Error(`Failed to add extra product ${variantId}`);
            }

            if (this.cart) {
              return extraData.sections || null;
            }
          } catch (e) {
            console.error(`Error adding extra product ${variantId}:`, e);
          }
          return null;
        };

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            let sections = null;
            if (isVoucherSelected) {
              const quantity = parseInt(formData.get('quantity')) || 1;

              // Voucher Fee only. This one reconciles cleanly with
              // syncAllAddons(), which requires the same variant with the same
              // 'For Variant' link value and therefore matches this line
              // instead of adding a second one.
              sections = await addExtraProduct('46396120596724', quantity, {
                'For Variant': formData.get('id')
              });

              // ── Logo Print Fee add removed 2026-09-10 ────────────────────
              // This used to add getVoucherVariantIdByQuantity(quantity) — a
              // TIERED variant — linked by 'For Variant'. syncAllAddons() adds
              // the print fee too, using the tier-1 id 46764302205172 linked by
              // 'For Part Number'. The link keys never matched, so the two were
              // never reconciled against each other:
              //
              //   qty < 25  both are id 46764302205172 but with different link
              //             values, so syncAllAddons added its own and then
              //             zeroed this one (the pre-existing bug where the
              //             print fee vanished on cart load)
              //   qty >= 25 the tiered ids are not in getTrackedIds(), so this
              //             line survived AND syncAllAddons added tier-1 on top
              //             — a double charge
              //
              // Voucher lines now get the print fee from syncAllAddons alone,
              // exactly like regular Buy Now lines. Quantity tiering is lost
              // in the process; see the handoff notes if it needs restoring.
              // ─────────────────────────────────────────────────────────────

              if (sections) response.sections = sections;
            }

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            }
            
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }


      /*onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        
        const voucherCheckbox = document.querySelector('input[name="vouchers_needed"]:checked');
        const isVoucherSelected = voucherCheckbox && voucherCheckbox.value == 'voucher';
        
        /*if (isVoucherSelected) {
            formData.append('properties[Vouchers]', 'Yes');
        }*/

        /*if (this.cart) {
            formData.append(
                'sections',
                this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
                publish(PUB_SUB_EVENTS.cartError, {
                    source: 'product-form',
                    productVariantId: formData.get('id'),
                    errors: response.errors || response.description,
                    message: response.message,
                });
                this.handleErrorMessage(response.description);

                const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
                if (!soldOutMessage) return;
                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButtonText.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
                this.error = true;
                return;
            } else if (!this.cart) {
                window.location = window.routes.cart_url;
                return;
            }

            if (isVoucherSelected) {
                const quantity = formData.get('quantity') || 1;
                const voucherFormData = new FormData();
                voucherFormData.append('id', '46396120596724');
                voucherFormData.append('quantity', quantity);
                voucherFormData.append('properties[For Variant]', formData.get('id'));

                const voucherConfig = fetchConfig('javascript');
                voucherConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
                delete voucherConfig.headers['Content-Type'];
                voucherConfig.body = voucherFormData;

                try {
                    const voucherResponse = await fetch(`${routes.cart_add_url}`, voucherConfig);
                    const voucherData = await voucherResponse.json();

                    if (voucherData.status) {
                        console.error('Failed to add voucher product:', voucherData);
                        throw new Error('Failed to add voucher product');
                    }

                    if (this.cart) {
                        response.sections = voucherData.sections || response.sections;
                    }
                } catch (e) {
                    console.error('Error adding voucher product:', e);
                }
            }

            if (!this.error)
                publish(PUB_SUB_EVENTS.cartUpdate, {
                    source: 'product-form',
                    productVariantId: formData.get('id'),
                    cartData: response,
                });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
                document.body.addEventListener(
                    'modalClosed',
                    () => {
                        setTimeout(() => {
                            this.cart.renderContents(response);
                        });
                    },
                    { once: true }
                );
                quickAddModal.hide(true);
            } else {
                this.cart.renderContents(response);
            }
        })
        .catch((e) => {
            console.error(e);
        })
        .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
        });
      }*/

      /*onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {

            console.log(response.properties)

            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }*/

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}